import { QueryClient } from "react-query";
import { enqueueSnackbar } from "notistack";

function onQueryError(error: unknown) {
  const status = (error as any)?.response?.status;
  if (status === 401 || status === 403) return;
  enqueueSnackbar("Something went wrong. Please try again.", {
    variant: "error",
    preventDuplicate: true,
  });
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      cacheTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
      onError: onQueryError,
    },
    mutations: {
      onError: onQueryError,
    },
  },
});

export async function getCachedOrFetch<T>(
  config: {
    queryKey: readonly string[];
    queryFn: (context: { signal?: AbortSignal }) => Promise<T>;
    staleTime: number;
    cacheTime: number;
  },
  signal?: AbortSignal,
  forceRefetch?: boolean
): Promise<T> {
  const { queryKey, queryFn, staleTime, cacheTime } = config;

  // Explicit force refetch
  if (forceRefetch) {
    return queryClient.fetchQuery(queryKey, () => queryFn({ signal }), {
      staleTime,
      cacheTime,
    });
  }

  const cachedData = queryClient.getQueryData<T>(queryKey);
  const queryState = queryClient.getQueryState(queryKey);

  if (queryState?.isInvalidated) {
    return queryClient.fetchQuery(queryKey, () => queryFn({ signal }), {
      staleTime,
      cacheTime,
    });
  }

  if (cachedData && queryState) {
    const now = Date.now();
    const updated = queryState.dataUpdatedAt ?? 0;
    const isStale = now - updated > staleTime;

    if (!isStale) {
      return cachedData;
    }
  }

  return queryClient.fetchQuery(queryKey, () => queryFn({ signal }), {
    staleTime,
    cacheTime,
  });
}

export function invalidateQueries(queryKey: readonly string[]) {
  return queryClient.invalidateQueries(queryKey);
}
